/**
 * A CUSTOMER IS THE JOBS THAT CARRY THEIR NAME.
 *
 * There is no customer table, and that is a decision rather than a shortcut.
 *
 * The alternative is a stored record that jobs point at, and it costs an
 * intake step: before you can photograph a house you have to say who it
 * belongs to, pick them off a list or create them. In this trade the name
 * arrives at the end, if at all - somebody knocks a door, takes a picture,
 * draws the roof, and only writes "Ridgeline HOA" when the quote goes out. A
 * model that demands the name first is a model that gets a hundred jobs called
 * Untitled.
 *
 * Grouping by the name that is already typed gives the same screen with none
 * of that, works backward over every job already saved, and is exactly as
 * correct: two jobs for the same person carry the same name because a person
 * typed the same name.
 *
 * WHAT IT COSTS. A name typed two ways is two customers. That is visible, and
 * the fix is to correct the spelling, which writes through to every one of
 * their jobs. A stored record would have to do the same write anyway: a quote
 * that has already printed has to keep saying what it said.
 *
 * WHERE THEY STAND IS DERIVED, NEVER STORED. Approved means somebody accepted
 * something: a job marked won, an invoice, or a deposit in hand. Waiting means
 * a quote has gone out with no answer. Nothing here is a flag anyone has to
 * remember to set, because the flag everybody forgets is the flag that lies.
 */

import type { ProjectSummary } from './db'

export type CustomerState = 'approved' | 'waiting' | 'draft' | 'lost'

export interface CustomerRow {
  /** The grouping key: the name, folded, so case and spacing do not split one. */
  key: string
  /** The name as most recently typed, which is the one to show. */
  name: string
  address: string
  phone: string
  /** Newest first, the same order the job list uses. */
  jobs: ProjectSummary[]
  /** What all their work comes to. */
  quoted: number
  /** What has actually been received. */
  collected: number
  /** What is still owed, on invoices only. */
  owed: number
  state: CustomerState
  /** The most recent save across their jobs, for sorting. */
  savedAt: string
}

/** Case, punctuation and spacing should not make two customers out of one. */
export const foldName = (s: string) =>
  s
    .toLowerCase()
    .replace(/[.,'"]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

/** Whether this job says somebody accepted something. */
const accepted = (p: ProjectSummary) =>
  p.status === 'won' || p.money?.kind === 'invoice' || (p.money?.paid ?? 0) > 0

export function customerState(jobs: ProjectSummary[]): CustomerState {
  if (jobs.some(accepted)) return 'approved'
  if (jobs.some((p) => p.status === 'quoted')) return 'waiting'
  if (jobs.length > 0 && jobs.every((p) => p.status === 'lost')) return 'lost'
  return 'draft'
}

export const STATE_LABEL: Record<CustomerState, string> = {
  approved: 'Approved',
  waiting: 'Not approved yet',
  draft: 'Not sent yet',
  lost: 'Lost',
}

/**
 * Every customer, newest first.
 *
 * Jobs with no name on them are left out rather than collected under a heading
 * like "No name". A mockup nobody has named is not a customer yet, and putting
 * forty of them at the top of this screen would bury the ones that are.
 */
export function groupCustomers(items: ProjectSummary[]): CustomerRow[] {
  const by = new Map<string, CustomerRow>()

  // Newest first going in, so the first job seen for a customer is the most
  // recent one, and its spelling, street and phone are the ones shown.
  const sorted = [...items].sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1))

  for (const p of sorted) {
    const name = p.customer.trim()
    if (!name) continue
    const key = foldName(name)
    let row = by.get(key)
    if (!row) {
      row = {
        key,
        name,
        address: p.address,
        phone: p.phone,
        jobs: [],
        quoted: 0,
        collected: 0,
        owed: 0,
        state: 'draft',
        savedAt: p.savedAt,
      }
      by.set(key, row)
    }
    row.jobs.push(p)
    // A street or a phone from an older job fills a gap the newest one left.
    if (!row.address.trim()) row.address = p.address
    if (!row.phone.trim()) row.phone = p.phone
    row.quoted += p.money?.total ?? 0
    row.collected += p.money?.paid ?? 0
    row.owed += p.money?.kind === 'invoice' ? p.money.balance : 0
  }

  const rows = [...by.values()]
  for (const r of rows) r.state = customerState(r.jobs)
  return rows.sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1))
}
